import { useSyncExternalStore } from "react";

// Sonner pins the toaster at 999999999, so demoting it is the only lever an overlay has. 45 is
// below the modal layer (50) and above the app chrome band (30s), tying with nothing -- a tie
// makes paint order follow portal mount order. Hiding it instead would silence aria-live.
export const TOASTER_BEHIND_DIALOG_Z_INDEX = 45;

const DIALOG_SELECTOR = '[role="dialog"][data-state="open"]';
const MODAL_OVERLAY_SELECTOR = ".modal-overlay";

const listeners = new Set<() => void>();
let isDialogOpen = false;
let isModalOverlayOpen = false;
let observers: MutationObserver[] = [];

const readState = () => {
  const nextDialog = !!document.querySelector(DIALOG_SELECTOR);
  const nextOverlay = !!document.querySelector(MODAL_OVERLAY_SELECTOR);
  if (nextDialog === isDialogOpen && nextOverlay === isModalOverlayOpen) {
    return;
  }
  isDialogOpen = nextDialog;
  isModalOverlayOpen = nextOverlay;
  for (const listener of listeners) {
    listener();
  }
};

const startObserving = () => {
  const attrObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target instanceof Element && mutation.target.matches('[role~="dialog"]')) {
        readState();
        return;
      }
    }
  });
  attrObserver.observe(document.body, { attributes: true, attributeFilter: ["data-state"], subtree: true });

  // Catches portal mount/unmount (Radix portals are direct children of body)
  const portalObserver = new MutationObserver(() => readState());
  portalObserver.observe(document.body, { childList: true });

  observers = [attrObserver, portalObserver];
  readState();
};

// One shared subscription: Radix stamps `data-state` on every tooltip, popover and tab, so a
// per-component observer pair would multiply that traffic by the number of consumers.
const subscribe = (onStoreChange: () => void) => {
  listeners.add(onStoreChange);
  if (observers.length === 0) {
    startObserving();
  }
  return () => {
    listeners.delete(onStoreChange);
    if (listeners.size === 0) {
      for (const observer of observers) {
        observer.disconnect();
      }
      observers = [];
    }
  };
};

const getServerSnapshot = () => false;
const getDialogSnapshot = () => isDialogOpen;
const getOverlaySnapshot = () => isModalOverlayOpen;

export const useIsDialogOpen = () => useSyncExternalStore(subscribe, getDialogSnapshot, getServerSnapshot);

export const useIsModalOverlayOpen = () => useSyncExternalStore(subscribe, getOverlaySnapshot, getServerSnapshot);
